/**
 * The owner's hosted files (ftl.dat, mods). /private/manifest (password) lists them with their
 * URLs: presigned links into the owner's S3-compatible bucket (no credentials needed, CORS
 * with Range), or this server's /private/ftl.dat and /private/mods/<name> (password). Servers
 * without the manifest answer 404 and the callers fall back to those two endpoints.
 *
 * Big files are fetched as parallel range requests but handed over as one in-order stream,
 * so they can go straight into OPFS: one TCP stream rarely fills a long-distance link
 * (Backblaze B2 → Brazil: ~11 MB/s alone, ~26 MB/s with 6), and buffering the whole file
 * (Multiverse's assets are ~790 MB) is not an option on phones.
 */

export interface OwnerFile {
  /** "ftl.dat" or "mods/<name>". */
  name: string;
  url: string;
  size: number;
}

/** Same-origin owner endpoints get the password; anything else (presigned links) nothing. */
export function ownerInit(url: string, password: string): RequestInit {
  return url.startsWith('/') ? { headers: { Authorization: `Basic ${btoa(`owner:${password}`)}`, 'X-FTL-Probe': '1' } } : {};
}

/**
 * The manifest, or null when the server predates it. Throws Error('HTTP 401') on a wrong
 * password and Error('HTTP 429') when rate-limited.
 */
export async function fetchOwnerManifest(password: string): Promise<OwnerFile[] | null> {
  const res = await fetch('/private/manifest', { ...ownerInit('/private/manifest', password), cache: 'no-store' });
  if (res.status === 401 || res.status === 429) throw new Error(`HTTP ${res.status}`);
  if (!res.ok || !(res.headers.get('content-type') ?? '').includes('application/json')) return null;
  const m = (await res.json()) as { files?: unknown };
  if (!Array.isArray(m.files)) return null;
  return m.files.filter((f): f is OwnerFile => !!f && typeof f.name === 'string' && typeof f.url === 'string' && typeof f.size === 'number');
}

export interface RangedOptions {
  /** Bytes per range request. */
  chunk?: number;
  /** Requests in flight (also the most chunks held in memory). */
  parallel?: number;
  /** Retries per range (network errors, 5xx, 408, 429). */
  retries?: number;
}

/**
 * `size` bytes of `url` as an in-order stream, downloaded as parallel range requests. Falls
 * back to the plain response body when the file is small or the server ignores Range.
 */
export function rangedStream(url: string, size: number, init: RequestInit = {}, { chunk = 8 << 20, parallel = 6, retries = 3 }: RangedOptions = {}): ReadableStream<Uint8Array> {
  // One controller per request: a Range-ignoring server's first response must survive the
  // aborting of the others.
  const controllers = new Map<number, AbortController>();
  let stopped = false;
  const stop = (keep = -1) => {
    stopped = true;
    for (const [i, c] of controllers) if (i !== keep) c.abort();
  };
  const plain = async (): Promise<ReadableStream<Uint8Array>> => {
    const res = await fetch(url, { cache: 'no-store', ...init });
    if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
    return res.body;
  };
  if (!(size > chunk)) return lazyStream(plain);

  const count = Math.ceil(size / chunk);
  const pending = new Map<number, Promise<Uint8Array | ReadableStream<Uint8Array>>>();
  let next = 0;
  let emitted = 0;
  let fallback: ReadableStreamDefaultReader<Uint8Array> | null = null;

  const getRange = async (i: number): Promise<Uint8Array | ReadableStream<Uint8Array>> => {
    const start = i * chunk;
    const end = Math.min(size, start + chunk) - 1;
    for (let attempt = 0; ; attempt++) {
      const ac = new AbortController();
      controllers.set(i, ac);
      try {
        const res = await fetch(url, {
          cache: 'no-store',
          ...init,
          signal: ac.signal,
          headers: { ...(init.headers as Record<string, string> | undefined), Range: `bytes=${start}-${end}` },
        });
        // A server ignoring Range sends the whole file: use that body as is.
        if (res.status === 200 && i === 0 && res.body) return res.body;
        if (res.status !== 206) throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });
        const buf = new Uint8Array(await res.arrayBuffer());
        if (buf.length !== end - start + 1) throw new Error('range response has the wrong length');
        return buf;
      } catch (e) {
        const status = (e as { status?: number }).status ?? 0;
        const retryable = !stopped && (status === 0 || status >= 500 || status === 408 || status === 429);
        if (!retryable || attempt >= retries) throw e;
        await new Promise((r) => setTimeout(r, 500 * 2 ** attempt));
      }
    }
  };

  const fill = () => {
    while (next < count && next - emitted < parallel && !fallback) {
      const p = getRange(next);
      p.catch(() => {}); // awaited in order by pull(); avoid unhandled-rejection noise meanwhile
      pending.set(next++, p);
    }
  };

  return new ReadableStream<Uint8Array>({
    async pull(ctrl) {
      try {
        if (fallback) {
          const { done, value } = await fallback.read();
          return void (done ? ctrl.close() : ctrl.enqueue(value));
        }
        if (emitted >= count) return void ctrl.close();
        fill();
        const p = pending.get(emitted)!;
        pending.delete(emitted);
        const got = await p;
        emitted++;
        if (got instanceof Uint8Array) {
          fill();
          return void ctrl.enqueue(got);
        }
        stop(0); // the other ranges are moot: the whole file is coming in this body
        fallback = got.getReader();
        const { done, value } = await fallback.read();
        done ? ctrl.close() : ctrl.enqueue(value);
      } catch (e) {
        stop();
        ctrl.error(e);
      }
    },
    cancel() {
      stop();
      void fallback?.cancel();
    },
  });
}

/** A stream whose source is opened on first read. */
function lazyStream(open: () => Promise<ReadableStream<Uint8Array>>): ReadableStream<Uint8Array> {
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  return new ReadableStream<Uint8Array>({
    async pull(ctrl) {
      try {
        reader ??= (await open()).getReader();
        const { done, value } = await reader.read();
        done ? ctrl.close() : ctrl.enqueue(value);
      } catch (e) {
        ctrl.error(e);
      }
    },
    cancel() {
      void reader?.cancel();
    },
  });
}
